import React, { useEffect, useRef, useState } from "react";
import { CountrySelect, StateSelect, CitySelect, GetCountries, GetState, GetCity } from "react-country-state-city";
import "react-country-state-city/dist/react-country-state-city.css";

type LocationVariant = "default" | "checkout";

interface LocationFieldsProps {
    userData: any;
    setUserData: React.Dispatch<React.SetStateAction<any>>;
    variant?: LocationVariant;
}

const LocationFields: React.FC<LocationFieldsProps> = ({
    userData,
    setUserData,
    variant = "default"
}) => {
    const [countryId, setCountryId] = useState<number>(0);
    const [stateId, setStateId] = useState<number>(0);

    const [selectedCountry, setSelectedCountry] = useState<any>(null);
    const [selectedState, setSelectedState] = useState<any>(null);
    const [selectedCity, setSelectedCity] = useState<any>(null);

    const [zipTouched, setZipTouched] = useState(false);
    const [locationTouched, setLocationTouched] = useState(false);

    const zipRef = useRef<HTMLInputElement | null>(null);

    const isCheckout = variant === "checkout";

    const inputClassName = isCheckout
        ? "w-full rounded-md border border-gray-300 bg-white px-3 py-3 text-sm text-[#181842] outline-none transition focus:border-secondary focus:ring-1 focus:ring-secondary"
        : "w-full rounded py-2.5 px-4 border-2 text-sm text-black outline-secondary bg-white";

    const labelClassName = isCheckout
        ? "mb-2 block text-sm font-medium text-[#181842]"
        : "mb-2 block text-sm font-semibold";

    const errorClassName = "mt-1 text-xs text-red-500";

    const countryName = String(userData?.country ?? "").trim().toLowerCase();
    const isUS =
        countryName === "united states" ||
        countryName === "united states of america";

    const zipValue = String(userData?.zip ?? "");
    const isCountryValid = Boolean(countryName);
    const isStateValid = Boolean(String(userData?.state ?? "").trim());
    const isCityValid = Boolean(String(userData?.city ?? "").trim());

    const zipIsValid = /^\d{5}(-\d{4})?$/.test(zipValue.trim());

    useEffect(() => {
        let isMounted = true;

        const initializeLocation = async () => {
            try {
                const countries = await GetCountries();

                if (!isMounted) return;

                const countryName = String(userData?.country ?? "")
                    .trim()
                    .toLowerCase();

                const stateName = String(userData?.state ?? "")
                    .trim()
                    .toLowerCase();

                const cityName = String(userData?.city ?? "")
                    .trim()
                    .toLowerCase();

                // Find existing country.
                const country = countryName
                    ? countries.find(
                        (item: any) =>
                            item.name?.trim().toLowerCase() === countryName ||
                            item.iso2?.trim().toLowerCase() === countryName
                    )
                    : countries.find(
                        (item: any) =>
                            item.iso2?.toUpperCase() === "US"
                    );

                if (!country || !isMounted) return;

                const currentCountryId = Number(country.id);

                setSelectedCountry(country);
                setCountryId(currentCountryId);

                // Find existing state.
                if (stateName) {
                    const states = await GetState(currentCountryId);

                    if (!isMounted) return;

                    const state = states.find(
                        (item: any) =>
                            item.name?.trim().toLowerCase() === stateName
                    );

                    if (state) {
                        const currentStateId = Number(state.id);

                        setSelectedState(state);
                        setStateId(currentStateId);

                        // Find existing city.
                        if (cityName) {
                            const cities = await GetCity(
                                currentCountryId,
                                currentStateId
                            );

                            if (!isMounted) return;

                            const city = cities.find(
                                (item: any) =>
                                    item.name?.trim().toLowerCase() === cityName
                            );

                            setSelectedCity(city ?? null);
                        }
                    }
                }

                // Only assign default country if none exists.
                if (!countryName) {
                    setUserData((previous: any) => ({
                        ...previous,
                        country: country.name,
                    }));
                }
            } catch (error) {
                console.error("Unable to initialize location:", error);
            }
        };

        // Do not initialize until location data is available.
        if (userData?.country) {
            void initializeLocation();
        }

        return () => {
            isMounted = false;
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        userData?.country,
        userData?.state,
        userData?.city
    ]);

    const handleCountryChange = (country: any) => {
        if (!country || typeof country !== "object" || !country.id || !country.name) {
            return;
        }

        setSelectedCountry(country);
        setCountryId(Number(country.id) || 0);
        setStateId(0);
        setUserData((previous: any) => ({
            ...previous,
            country: country.name,
            state: "",
            city: "",
            zip: "",
        }));
        setZipTouched(false);
        setLocationTouched(true);
    };

    const handleStateChange = (state: any) => {
        setStateId(Number(state?.id) || 0);
        setUserData((previous: any) => ({
            ...previous,
            state: state?.name ?? "",
            city: "",
        }));
        setLocationTouched(true);
    };

    const handleCityChange = (city: any) => {
        setUserData((previous: any) => ({
            ...previous,
            city: city?.name ?? "",
        }));
        setLocationTouched(true);
    };

    const handleZipChange = (value: string) => {
        setUserData((previous: any) => ({
            ...previous,
            zip: isUS
                ? value.replace(/[^\d-]/g, "").slice(0, 10)
                : value.slice(0, 14),
        }));
    };

    useEffect(() => {
        if (!zipRef.current) return;

        if (zipIsValid) {
            zipRef.current.setCustomValidity("");
        } else {
            zipRef.current.setCustomValidity(
                zipValue.trim() === ""
                    ? "ZIP / Postal Code is required."
                    : isUS
                        ? "Enter a valid US ZIP code (12345 or 12345-6789)."
                        : "Enter a valid postal code (2–14 letters, numbers, spaces, or hyphens)."
            );
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zipIsValid, zipValue, isUS]);

    return (
        <>
            <div>
                <label className={labelClassName}>
                    Country
                </label>
                <CountrySelect
                    key={`country-${selectedCountry?.id ?? "loading"}`}
                    defaultValue={selectedCountry ?? undefined}
                    containerClassName="w-full"
                    inputClassName={inputClassName}
                    placeHolder="Select Country"
                    required
                    onChange={handleCountryChange}
                />
                {locationTouched && !isCountryValid && (
                    <p className={errorClassName}>Please select a country.</p>
                )}
            </div>

            <div>
                <label className={labelClassName}>
                    State / Province
                </label>
                <StateSelect
                    key={`state-${countryId}-${selectedState?.id ?? "none"}`}
                    countryid={countryId}
                    defaultValue={selectedState ?? undefined}
                    containerClassName="w-full"
                    inputClassName={inputClassName}
                    placeHolder={countryId ? "Select State / Province" : "Select country first"}
                    disabled={!countryId}
                    required
                    onChange={handleStateChange}
                />
                {locationTouched && !isStateValid && (
                    <p className={errorClassName}>Please select a state or province.</p>
                )}
            </div>

            <div>
                <label className={labelClassName}>
                    City
                </label>
                <CitySelect
                    key={`city-${countryId}-${stateId}-${selectedCity?.id ?? "none"}`}
                    countryid={countryId}
                    stateid={stateId}
                    defaultValue={selectedCity ?? undefined}
                    containerClassName="w-full"
                    inputClassName={inputClassName}
                    placeHolder={stateId ? "Select City" : "Select state first"}
                    disabled={!stateId}
                    required
                    onChange={handleCityChange}
                />
                {locationTouched && !isCityValid && (
                    <p className={errorClassName}>Please select a city.</p>
                )}
            </div>

            <div>
                <label className={labelClassName} htmlFor="location-postal-code">
                    Zip / Postal Code
                </label>

                <input
                    ref={zipRef}
                    id="location-postal-code"
                    name="postalCode"
                    type="text"
                    required
                    value={zipValue}
                    onChange={(event) => handleZipChange(event.target.value)}
                    onBlur={() => setZipTouched(true)}
                    placeholder={isUS ? "12345 or 12345-6789" : "Enter postal code"}
                    className={`${inputClassName} ${zipTouched && !zipIsValid ? "border-red-400" : ""
                        }`}
                    inputMode={isUS ? "numeric" : "text"}
                    autoComplete="postal-code"
                    aria-invalid={(zipTouched || Boolean(zipValue)) && !zipIsValid}
                />
                {(zipTouched || Boolean(zipValue)) && !zipIsValid && (
                    <p className={errorClassName}>
                        Enter a valid US ZIP code (12345 or 12345-6789).
                    </p>
                )}
            </div>
        </>
    );
};

export default LocationFields;
